import { motion } from 'framer-motion';

/**
 * EvisStar - Radiating star icon for Evis AI branding
 * A colorful star with animated glow/pulse effect
 */
const EvisStar = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: { outer: 32, inner: 16 },
    md: { outer: 48, inner: 24 },
    lg: { outer: 80, inner: 40 },
    xl: { outer: 120, inner: 60 },
  };
  
  const { outer, inner } = sizes[size] || sizes.md;
  
  return (
    <div className={`relative ${className}`} style={{ width: outer, height: outer }}>
      {/* Radiating glow rings */}
      <motion.div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(251,191,36,0.3) 0%, rgba(99,102,241,0.2) 50%, transparent 70%)',
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.5, 0.8, 0.5],
        }}
        transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="absolute inset-0 rounded-full"
        style={{
          background: 'radial-gradient(circle, rgba(251,191,36,0.2) 0%, rgba(139,92,246,0.15) 50%, transparent 70%)',
        }}
        animate={{
          scale: [1.1, 1.4, 1.1],
          opacity: [0.3, 0.6, 0.3],
        }}
        transition={{
          duration: 2.5,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 0.3,
        }}
      />
      
      {/* Star SVG */}
      <motion.svg
        viewBox="0 0 24 24"
        fill="none"
        className="absolute"
        style={{
          width: inner,
          height: inner,
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
        }}
        animate={{
          rotate: [0, 5, -5, 0],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      >
        <defs>
          <linearGradient id="evisStarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fbbf24" />
            <stop offset="50%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#6366f1" />
          </linearGradient>
          <filter id="evisGlow">
            <feGaussianBlur stdDeviation="1" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        <path
          d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
          fill="url(#evisStarGradient)"
          filter="url(#evisGlow)"
        />
      </motion.svg>
    </div>
  );
};

/**
 * EvisLogo - Full Evis branding with star and text
 */
export const EvisLogo = ({ size = 'md', showTagline = false, className = '' }) => {
  const textSizes = {
    sm: 'text-lg',
    md: 'text-2xl',
    lg: 'text-4xl',
    xl: 'text-5xl',
  };
  
  const taglineSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
    xl: 'text-lg',
  };
  
  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <EvisStar size={size} />
      <div>
        <h2 className={`font-bold ${textSizes[size]} bg-gradient-to-r from-amber-400 via-amber-300 to-indigo-400 bg-clip-text text-transparent`}>
          Evis
        </h2>
        {showTagline && (
          <p className={`${taglineSizes[size]} text-slate-400`}>
            Your AI Concierge
          </p>
        )}
      </div>
    </div>
  );
};

export default EvisStar;
